import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Button, DatePicker, ConfigProvider, message} from "antd";
import {UploadOutlined} from "@ant-design/icons";
import moment from "moment-timezone";
import {partition} from "lodash";
import {RoundedTable, SearchInputWithIcon, ConfirmationRequiredWrapper} from "../../../components";
import {TimelineModal} from "../../../components/Timeline";
import {ProviderAccountingDocumentModal} from "../../../provider/ProviderAccountingDocumentModal";
import {momentHebrewFormat} from "../../../utils/DateFormat";
import {useRequest} from "../../../utils/hooks";
import getColumns from "./columns.jsx";
import {OrderChangesHistoryModal} from "../../AdminOrders/OrderChangesHistoryModal";
import {useHistory} from "react-router-dom";
import "./delivery-provider-invoice-table.css";
import {NOOP} from "../../../utils/NOOP";
import {getSummaryData} from "../utils";
import {findInvoiceWithDelivery} from "../utils";
import {InvoicesStatusToNameAndColor} from "../../../provider/ProviderManageInvoices/consts";
import {TrashIcon} from "../../../icons";

const DeliveryProviderInvoiceTable = ({
    deliveries,
    loadingDeliveries,
    selectedMonth,
    setSelectedMonth = NOOP,
    uploadInvoice,
    invoices,
    logsURI,
    shouldSelectInvoicedDeliveries = false,
    deleteInvoice
}) => {
    const history = useHistory();
    const [visibleHistoryModal, setVisibleHistoryModal] = useState(null);
    const [confirmInvoiceData, setConfirmInvoiceData] = useState(null);
    const [searchInput, setSearchInput] = useState("");
    const [selectedDeliveries, setSelectedDeliveries] = useState([]);
    const [logsModalVisible, setLogsModalVisible] = useState(false);
    const [showInvoiceOfMonthModal, setShowInvoiceOfMonthModal] = useState(false);

    const [logsFromServer] = useRequest(logsURI, "GET", null, [], !!logsURI && logsModalVisible);

    const invoiceLogs = useMemo(() => {
        if (!logsFromServer?.length) return [];

        return logsFromServer.map(log => {
            const text = (
                <div className="provider-manage-invoices-logs-modal">
                    <div className="text-container">
                        <div className="text">
                            <span style={{color: InvoicesStatusToNameAndColor[log.invoice?.status]?.color}}>
                                <u>{InvoicesStatusToNameAndColor[log.invoice?.status]?.name}</u>
                                {log.invoice?.declineMessage ? ` - ${log.invoice.declineMessage}` : null}
                                {log.specialLogType === "DeleteLog" ? (
                                    <span className="error-text">{" חשבונית נמחקה"}</span>
                                ) : null}
                            </span>
                            <span>({log.editor.email})</span>
                            <span>|</span>
                            <a href={log.invoice?.file?.fileUrl} target="_blank" rel="noopener noreferrer">
                                מספר חשבונית: {log.invoice?.invoiceNumber}
                            </a>
                            <span>|</span>
                            <span>{moment(log.createdAt).format("DD/MM/YYYY")}</span>
                        </div>
                        <div>
                            <span>₪</span>
                            <span>{log.invoice?.price}</span>
                        </div>
                    </div>
                </div>
            );

            const icon = (
                <div
                    className="provider-manage-invoices-logs-modal-icon"
                    style={{backgroundColor: InvoicesStatusToNameAndColor[log.invoice?.status]?.color}}
                />
            );

            return {
                id: log.logId,
                icon,
                text
            };
        });
    }, [logsFromServer]);

    const invoicesOfMonth = useMemo(() => {
        if (!invoices?.length) return [];

        return invoices.map(invoice => {
            const text = (
                <div className="provider-manage-invoices-logs-modal">
                    <div className="text-container">
                        <div className="text">
                            <span style={{color: InvoicesStatusToNameAndColor[invoice.status]?.color}}>
                                <u>{InvoicesStatusToNameAndColor[invoice.status]?.name}</u>
                            </span>
                            <span>|</span>
                            <a href={invoice.file?.fileUrl} target="_blank" rel="noopener noreferrer">
                                מספר חשבונית: {invoice.invoiceNumber}
                            </a>
                            <span>|</span>
                            <span>{moment(invoice.createdAt).format("DD/MM/YYYY")}</span>
                        </div>

                        <div className="end-text">
                            <span>₪</span>
                            <span>{invoice.price}</span>
                            {deleteInvoice ? (
                                <ConfirmationRequiredWrapper
                                    title="למחוק חשבונית?"
                                    onYes={() => deleteInvoice(invoice.invoiceId, invoice.deliveryProviderId)}>
                                    <TrashIcon />
                                </ConfirmationRequiredWrapper>
                            ) : null}
                        </div>
                    </div>
                    {invoice.declineMessage && (
                        <div>
                            <span>סיבת הדחייה: </span>
                            <span>{invoice.declineMessage}</span>
                        </div>
                    )}
                </div>
            );

            const icon = (
                <div
                    className="provider-manage-invoices-logs-modal-icon"
                    style={{backgroundColor: InvoicesStatusToNameAndColor[invoice.status]?.color}}
                />
            );

            return {
                id: invoice.invoiceId,
                icon,
                text
            };
        });
    }, [invoices, deleteInvoice]);

    const filteredDeliveries = useMemo(
        () =>
            searchInput
                ? deliveries?.filter(delivery =>
                      ["proposalId", "companyName"].some(key => String(delivery[key])?.includes(searchInput))
                  )
                : deliveries,
        [searchInput, deliveries]
    );

    const summaryData = useMemo(() => getSummaryData(selectedDeliveries), [selectedDeliveries]);

    const selectAll = useCallback(() => {
        if (filteredDeliveries?.length) {
            const [invoicedDeliveries, notInvoicedDeliveries] = partition(filteredDeliveries, ({deliveryId}) =>
                findInvoiceWithDelivery(deliveryId, invoices)
            );

            if (shouldSelectInvoicedDeliveries) {
                setSelectedDeliveries(invoicedDeliveries);
            } else {
                setSelectedDeliveries(notInvoicedDeliveries);
            }
        } else {
            setSelectedDeliveries([]);
        }
    }, [shouldSelectInvoicedDeliveries, filteredDeliveries, invoices]);

    useEffect(() => {
        selectAll();
    }, [selectAll]);

    const columns = useMemo(
        () =>
            getColumns({
                invoices,
                onViewDelivery: delivery => history?.push(`/delivery/${delivery.deliveryId}`),
                onShowHistoryOrder: delivery => setVisibleHistoryModal(delivery.orderId)
            }),
        [invoices, history]
    );

    const handleUploadInvoice = useCallback(() => {
        const deliveryIds = selectedDeliveries.map(d => d.deliveryId);
        const totalPrice = selectedDeliveries.reduce(
            (acc, d) => acc + (d.price ?? 0) - (d.order.deliveryProviderDiscount ?? 0),
            0
        );

        setConfirmInvoiceData({
            deliveries: deliveryIds,
            price: totalPrice,
            orders: deliveryIds,
            totalDeliveries: deliveryIds.length,
            month: selectedMonth.toDate(),
            monthInHebrew: momentHebrewFormat(selectedMonth, "MMMM YYYY")
        });
    }, [selectedDeliveries, selectedMonth]);

    const handleConfirmInvoice = useCallback(
        async invoiceData => {
            if (!confirmInvoiceData) return;

            const invoicePayload = {
                ...invoiceData,
                deliveries: confirmInvoiceData.deliveries,
                totalDeliveries: confirmInvoiceData.totalDeliveries,
                month: confirmInvoiceData.month
            };

            await uploadInvoice(invoicePayload);
            setConfirmInvoiceData(null);
            setSelectedDeliveries([]);
        },
        [confirmInvoiceData, uploadInvoice]
    );

    return (
        <ConfigProvider direction="rtl">
            <div className="delivery-provider-invoice-table">
                <div className="delivery-provider-invoice-table-header">
                    <div className="delivery-provider-invoice-table-header-left">
                        <DatePicker
                            format="MM-YYYY"
                            placeholder="בחרו חודש"
                            onChange={setSelectedMonth}
                            value={selectedMonth}
                            picker="month"
                            allowClear={false}
                        />
                        <SearchInputWithIcon searchInput={searchInput} setSearchInput={setSearchInput} />
                    </div>
                    <span onClick={() => setShowInvoiceOfMonthModal(true)}>ניהול חשבוניות</span>
                </div>

                <RoundedTable
                    className="provider-manage-invoices-table"
                    rowKey="deliveryId"
                    scroll={{x: 1200}}
                    loading={loadingDeliveries}
                    dataSource={filteredDeliveries}
                    columns={columns}
                    rowSelection={{
                        selectedRowKeys: selectedDeliveries.map(d => d.deliveryId),
                        onSelect: (_, __, newSelectedRows) => setSelectedDeliveries(newSelectedRows),
                        onSelectAll: select => (select ? selectAll() : setSelectedDeliveries([])),
                        getCheckboxProps: ({deliveryId}) => ({
                            disabled: findInvoiceWithDelivery(deliveryId, invoices) ? "linked-to-invoice-row" : null
                        })
                    }}
                    pagination={false}
                    rowClassName={({deliveryId}) =>
                        findInvoiceWithDelivery(deliveryId, invoices) ? "linked-to-invoice-row" : null
                    }
                    summary={() => {
                        return (
                            <RoundedTable.Summary.Row>
                                <RoundedTable.Summary.Cell colSpan={6}>
                                    סיכום לקבלה לא כולל מע״מ
                                </RoundedTable.Summary.Cell>
                                <RoundedTable.Summary.Cell colSpan={2}>
                                    {summaryData?.deliveryPrice
                                        ? `${summaryData.deliveryPrice.toLocaleString()} ₪ `
                                        : null}
                                </RoundedTable.Summary.Cell>
                                <RoundedTable.Summary.Cell colSpan={1}>
                                    {summaryData?.totalDeliveryProviderDiscount
                                        ? `${summaryData.totalDeliveryProviderDiscount.toLocaleString() * -1} ₪`
                                        : `0 ₪`}
                                </RoundedTable.Summary.Cell>
                                <RoundedTable.Summary.Cell colSpan={2}>
                                    {summaryData?.totalPrice ? `${summaryData?.totalPrice.toLocaleString()} ₪` : null}
                                </RoundedTable.Summary.Cell>
                            </RoundedTable.Summary.Row>
                        );
                    }}
                />

                {!!uploadInvoice && (
                    <div className="provider-manage-invoices-bottom-bar">
                        <span onClick={() => setLogsModalVisible(true)}>
                            מעקב פעולות {momentHebrewFormat(selectedMonth, "MMMM YYYY")}
                        </span>
                        <div className="provider-manage-invoices-button-container">
                            <Button
                                type="primary"
                                icon={<UploadOutlined />}
                                onClick={handleUploadInvoice}
                                disabled={!selectedDeliveries.length}>
                                טעינת חשבונית עבור {momentHebrewFormat(selectedMonth, "MMMM YYYY")}
                            </Button>
                        </div>
                    </div>
                )}

                <ProviderAccountingDocumentModal
                    confirmInvoiceData={confirmInvoiceData}
                    onSave={handleConfirmInvoice}
                    onClose={() => setConfirmInvoiceData(null)}
                    isExemptDealerType={false}
                />

                <TimelineModal
                    visible={showInvoiceOfMonthModal}
                    title={`ניהול חשבוניות: ${momentHebrewFormat(selectedMonth, "MMMM YYYY")}`}
                    list={invoicesOfMonth}
                    onClose={() => setShowInvoiceOfMonthModal(false)}
                />
                <TimelineModal
                    visible={logsModalVisible}
                    onClose={() => setLogsModalVisible(false)}
                    title={`מעקב פעולות: ${momentHebrewFormat(selectedMonth, "MMMM YYYY")}`}
                    list={invoiceLogs}
                />

                <OrderChangesHistoryModal
                    visible={!!visibleHistoryModal}
                    orderId={visibleHistoryModal}
                    onClose={() => setVisibleHistoryModal(null)}
                />
            </div>
        </ConfigProvider>
    );
};

export default DeliveryProviderInvoiceTable;
